import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AdminTitle } from "@/admin/AdminLayout";
import { Empty, Listing, fmtDay, useList } from "@/admin/hr/common";

const NEW = { title: "", organization: "", date: "", location: "", description: "", active: true };

function JobDialog({ job, onClose, onSaved }) {
  const [v, setV] = useState(job?.id ? { ...NEW, ...job } : NEW);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { title: v.title, organization: v.organization, date: v.date, location: v.location, description: v.description, active: v.active };
    try {
      if (job?.id) await api.put(`/admin/hr/jobs/${job.id}`, body);
      else await api.post("/admin/hr/jobs", body);
      toast.success("Görev kaydedildi");
      onSaved();
      onClose();
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };
  const inp = (k, l, p = {}) => <label className="text-sm"><span className="field-label">{l}</span><input value={v[k]} onChange={set(k)} className="field-input" data-testid={`job-${k}`} {...p} /></label>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="job-dialog">
        <DialogHeader><DialogTitle>{job?.id ? "Görevi düzenle" : "Yeni görev / ilan"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">{inp("title", "Görev veya ilan adı *", { required: true, minLength: 3, maxLength: 150 })}</div>
          {inp("organization", "Etkinlik / kurum adı", { maxLength: 150 })}
          {inp("date", "Tarih", { type: "date" })}
          <div className="sm:col-span-2">{inp("location", "Görev yeri", { maxLength: 150 })}</div>
          <label className="text-sm sm:col-span-2"><span className="field-label">Açıklama</span><textarea rows={3} maxLength={2000} value={v.description} onChange={set("description")} className="field-input" data-testid="job-description" /></label>
          <label className="flex items-center gap-3 text-sm sm:col-span-2"><Switch checked={v.active} onCheckedChange={(c) => setV({ ...v, active: c })} data-testid="job-active-switch" /> {v.active ? "Aktif – başvuru formunda listelenir" : "Pasif – yeni başvurulara kapalı"}</label>
          <p className="text-xs text-zinc-500 sm:col-span-2">Tarihi geçmiş görevler formda otomatik olarak gösterilmez. Tarih boş bırakılırsa görev pasif yapılana kadar listelenir.</p>
          <button className="btn-red sm:col-span-2" data-testid="job-save-btn">Kaydet</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function jobState(j) {
  if (!j.active) return ["Pasif", "bg-zinc-200 text-zinc-700"];
  if (j.expired) return ["Süresi doldu", "bg-orange-100 text-orange-800"];
  return ["Aktif", "bg-emerald-100 text-emerald-800"];
}

function JobRow({ j, onEdit, reload }) {
  const toggle = async (active) => {
    try {
      await api.patch(`/admin/hr/jobs/${j.id}`, { active });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    if (!window.confirm(`"${j.title}" silinsin mi? Bu göreve yapılmış başvurulardaki görev bilgisi korunur.`)) return;
    try {
      await api.delete(`/admin/hr/jobs/${j.id}`);
      toast.success("Görev silindi");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const state = jobState(j);
  return (
    <tr data-testid={`job-row-${j.id}`}>
      <td className="px-3 py-3"><p className="font-semibold text-zinc-900">{j.title}</p>{j.description && <p className="line-clamp-1 text-xs text-zinc-500">{j.description}</p>}</td>
      <td className="px-3 py-3 text-zinc-600">{j.organization || "-"}</td>
      <td className="whitespace-nowrap px-3 py-3">{j.date ? fmtDay(j.date) : "-"}</td>
      <td className="px-3 py-3 text-zinc-600">{j.location || "-"}</td>
      <td className="px-3 py-3"><Link to={`/admin/basvurular?job_id=${j.id}`} className="font-bold text-[color:var(--red)] underline" data-testid={`job-count-${j.id}`}>{j.applications}</Link></td>
      <td className="px-3 py-3"><span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${state[1]}`} data-testid={`job-state-${j.id}`}>{state[0]}</span></td>
      <td className="whitespace-nowrap px-3 py-3">
        <Switch checked={j.active} onCheckedChange={toggle} aria-label="Aktif/pasif" data-testid={`job-toggle-${j.id}`} />
        <button onClick={() => onEdit(j)} className="ml-2 rounded p-1.5 hover:bg-zinc-100" aria-label="Düzenle" data-testid={`job-edit-${j.id}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={del} className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label="Sil" data-testid={`job-delete-${j.id}`}><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

export default function Jobs() {
  const [rows, load] = useList("/admin/hr/jobs", {});
  const [edit, setEdit] = useState(null);
  return (
    <div>
      <AdminTitle title="Görev ve İlanlar" desc="Aktif görevler iş başvuru formundaki 'Başvurulan Görev / İlan' listesinde otomatik görünür.">
        <button onClick={() => setEdit({})} className="btn-red !py-2" data-testid="job-add-btn"><Plus className="h-4 w-4" /> Yeni Görev / İlan</button>
      </AdminTitle>
      <Listing rows={rows} empty={<Empty text="Henüz görev veya ilan eklenmedi." testid="jobs-empty" />}>
        <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
          <table className="w-full text-sm" data-testid="jobs-table">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500"><tr>{["Görev / İlan", "Etkinlik / Kurum", "Tarih", "Görev Yeri", "Başvuru", "Durum", ""].map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-zinc-100">{(rows || []).map((j) => <JobRow key={j.id} j={j} onEdit={setEdit} reload={load} />)}</tbody>
          </table>
        </div>
      </Listing>
      {edit && <JobDialog job={edit} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
